var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as GraphQL from "./generated/graphqlClient";
import { ThermostatSettingSchema } from "@grumpycorp/warm-and-fuzzy-shared";
import cloneDeep from "clone-deep";
import fastCompare from "react-fast-compare";
import moment from "moment";
//
// Local helpers
//
const compareMaybeDate = (lhs, rhs) => {
    if (((lhs === null || lhs === void 0 ? void 0 : lhs.valueOf()) || 0) < ((rhs === null || rhs === void 0 ? void 0 : rhs.valueOf()) || 0)) {
        return -1;
    }
    if (((lhs === null || lhs === void 0 ? void 0 : lhs.valueOf()) || 0) > ((rhs === null || rhs === void 0 ? void 0 : rhs.valueOf()) || 0)) {
        return 1;
    }
    return 0;
};
const compareMaybeNumber = (lhs, rhs) => {
    if ((lhs || 0) < (rhs || 0)) {
        return -1;
    }
    if ((lhs || 0) > (rhs || 0)) {
        return 1;
    }
    return 0;
};
export var ThermostatSettingsHelpers;
(function (ThermostatSettingsHelpers) {
    //
    // IndexedThermostatSetting
    //
    //
    // MutableSettingsStore
    //
    class MutableSettingsStore {
        constructor(thermostatSettingsStore, thermostatSettings, setIsSaving) {
            this.thermostatSettingsStore = thermostatSettingsStore;
            this.thermostatSettings = thermostatSettings;
            this.setIsSaving = setIsSaving;
            // Deep-clone settings array so we can sort it here and mutate it in child components;
            // also inject indexes to map settings back to store definition.
            // (Deep-cloning may be overkill, but we want to be sure not to mutate the store directly at any point.)
            this.indexedSettings = cloneDeep(thermostatSettings.settings).map((thermostatSetting, index) => {
                return Object.assign(Object.assign({}, thermostatSetting), { index });
            });
            const holdSettings = this.indexedSettings
                .filter(setting => setting.type === GraphQL.ThermostatSettingType.Hold)
                .sort((lhs, rhs) => compareMaybeDate(lhs.holdUntil, rhs.holdUntil));
            const scheduledSettings = this.indexedSettings
                .filter(setting => setting.type === GraphQL.ThermostatSettingType.Scheduled)
                .sort((lhs, rhs) => compareMaybeNumber(lhs.atMinutesSinceMidnight, rhs.atMinutesSinceMidnight));
            this.orderedSettings = holdSettings.concat(scheduledSettings);
            const newHoldSettingTemplateBase = holdSettings.length > 0
                ? holdSettings[holdSettings.length - 1]
                : {
                    type: GraphQL.ThermostatSettingType.Hold,
                    index: -1,
                    // Hold settings
                    holdUntil: new Date(),
                    // Scheduled settings
                    atMinutesSinceMidnight: 0,
                    daysOfWeek: [],
                    // For all types
                    allowedActions: [],
                    setPointHeat: 18,
                    setPointCool: 22,
                    setPointCirculateAbove: ThermostatSettingSchema.SetPointRange.max,
                    setPointCirculateBelow: ThermostatSettingSchema.SetPointRange.min,
                };
            // Whichever source the new hold setting comes from, default to expiring four hours from now
            this.newHoldSettingTemplate = Object.assign(Object.assign({}, newHoldSettingTemplateBase), { holdUntil: moment()
                    .add(4, "hours")
                    .toDate() });
            this.newScheduledSettingTemplate =
                scheduledSettings.length > 0
                    ? scheduledSettings[scheduledSettings.length - 1]
                    : {
                        type: GraphQL.ThermostatSettingType.Scheduled,
                        index: -1,
                        // Hold settings
                        holdUntil: new Date(0),
                        // Scheduled settings
                        atMinutesSinceMidnight: 0,
                        daysOfWeek: ThermostatSettingSchema.DaysOfWeek,
                        // For all types
                        allowedActions: [],
                        setPointHeat: 18,
                        setPointCool: 22,
                        setPointCirculateAbove: ThermostatSettingSchema.SetPointRange.max,
                        setPointCirculateBelow: ThermostatSettingSchema.SetPointRange.min,
                    };
        }
        //
        // Mutation callbacks
        //
        onSave(updatedThermostatSetting) {
            return __awaiter(this, void 0, void 0, function* () {
                return this.onMutate((mutatedSettingsArray) => {
                    mutatedSettingsArray[updatedThermostatSetting.index] = updatedThermostatSetting;
                    return mutatedSettingsArray;
                });
            });
        }
        onAdd(addedThermostatSetting) {
            return __awaiter(this, void 0, void 0, function* () {
                return this.onMutate((mutatedSettingsArray) => {
                    if (addedThermostatSetting.type === GraphQL.ThermostatSettingType.Hold) {
                        // Adding a new Hold -> remove any existing expired Hold settings
                        mutatedSettingsArray = mutatedSettingsArray.filter(setting => 
                        // Keep all non-Hold settings
                        setting.type !== GraphQL.ThermostatSettingType.Hold ||
                            // Keep Hold settings that expire in the future
                            (setting.holdUntil && setting.holdUntil.valueOf() >= Date.now()));
                    }
                    mutatedSettingsArray.push(addedThermostatSetting);
                    return mutatedSettingsArray;
                });
            });
        }
        onRemove(removedThermostatSetting) {
            return __awaiter(this, void 0, void 0, function* () {
                return this.onMutate((mutatedSettingsArray) => {
                    mutatedSettingsArray.splice(removedThermostatSetting.index, 1);
                    return mutatedSettingsArray;
                });
            });
        }
        onMutate(mutateFn) {
            return __awaiter(this, void 0, void 0, function* () {
                this.setIsSaving(true);
                // Create local copy of settings array to update
                const mutatedSettingsArray = this.indexedSettings.slice();
                // Mutate array
                const updatedSettingsArray = mutateFn(mutatedSettingsArray);
                // Project array of IndexedThermostatSetting back to ThermostatSetting
                const mutatedSettings = Object.assign(Object.assign({}, this.thermostatSettings), { settings: updatedSettingsArray.map(setting => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { index } = setting, rest = __rest(setting, ["index"]);
                        return Object.assign({}, rest);
                    }) });
                // Update in store
                yield this.thermostatSettingsStore.updateItem(mutatedSettings);
                this.setIsSaving(false);
            });
        }
    }
    ThermostatSettingsHelpers.MutableSettingsStore = MutableSettingsStore;
    //
    // HoldUntil
    //
    ThermostatSettingsHelpers.HoldUntilForeverSentinel = new Date(Math.pow(2, 31) * 1000); // cheap sentinel value - close enough...
    ThermostatSettingsHelpers.HoldUntilHoursFromNowOptions = [1, 2, 4, 8, 12, 24];
    ThermostatSettingsHelpers.FormatHoldUntil = (holdUntil) => {
        if (holdUntil.valueOf() < Date.now()) {
            return "(expired)";
        }
        if (holdUntil.valueOf() >= ThermostatSettingsHelpers.HoldUntilForeverSentinel.valueOf()) {
            return "forever";
        }
        return `until ${moment(holdUntil).fromNow(true)} from now`;
    };
    //
    // DaysOfWeek
    //
    ThermostatSettingsHelpers.IsWeekend = (dayOfWeek) => {
        return [GraphQL.DayOfWeek.Saturday, GraphQL.DayOfWeek.Sunday].includes(dayOfWeek);
    };
    ThermostatSettingsHelpers.WeekdayDays = ThermostatSettingSchema.DaysOfWeek.filter(dayOfWeek => !ThermostatSettingsHelpers.IsWeekend(dayOfWeek));
    ThermostatSettingsHelpers.WeekendDays = ThermostatSettingSchema.DaysOfWeek.filter(dayOfWeek => ThermostatSettingsHelpers.IsWeekend(dayOfWeek));
    ThermostatSettingsHelpers.FormatDaysOfWeekList = (daysOfWeek) => {
        // Rebuild in-day-order array for display and comparison purposes since they may be returned in arbitrary order
        const inOrderDaysOfWeek = ThermostatSettingSchema.DaysOfWeek.filter(dayOfWeek => daysOfWeek.includes(dayOfWeek));
        if (inOrderDaysOfWeek.length === ThermostatSettingSchema.DaysOfWeek.length) {
            return "Everyday";
        }
        if (fastCompare(inOrderDaysOfWeek, ThermostatSettingsHelpers.WeekdayDays)) {
            return "Weekdays";
        }
        if (fastCompare(inOrderDaysOfWeek, ThermostatSettingsHelpers.WeekendDays)) {
            return "Weekends";
        }
        return inOrderDaysOfWeek.map(day => day.substr(0, 3)).join(", ");
    };
    //
    // AtMinutesSinceMidnight
    //
    ThermostatSettingsHelpers.FormatMinutesSinceMidnight = (value) => String(Math.floor(value / 60)).padStart(2, "0") +
        ":" +
        String(Math.round(value % 60)).padStart(2, "0");
    ThermostatSettingsHelpers.ParseMinutesSinceMidnight = (value) => {
        const [hours, minutes] = value.split(":");
        return Number.parseInt(hours) * 60 + Number.parseInt(minutes);
    };
})(ThermostatSettingsHelpers || (ThermostatSettingsHelpers = {}));
//# sourceMappingURL=thermostatSettingsHelpers.js.map